import { useState, useEffect, useCallback } from "react";
import { C, S } from "../customers/theme";
import { GetStartedCTA } from "../components/DemoDataControls";
import {
  getProducts, createProduct, updateProduct, deleteProduct,
  getProductCategories, PRODUCT_STATUSES,
} from "../lib/products";
import ProductForm from "./ProductForm";
import CategoryManager from "./CategoryManager";
import ProductCatalogueEditor from "./ProductCatalogueEditor";

const PAGE_SIZE = 25;
const money = (n) => (n == null ? "—" : `€${Number(n).toFixed(2)}`);
const STATUS_COLOR = { active: C.success, inactive: C.warning, archived: C.textDim };

function StatusBadge({ status }) {
  return <span style={S.pill(STATUS_COLOR[status] || C.textDim)}>{status || "—"}</span>;
}

// Sortable column header (accessible: button + aria-sort).
function Th({ label, col, sortBy, sortDir, onSort, style }) {
  const active = sortBy === col;
  return (
    <th style={{ ...S.th, ...style }} aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
      <button onClick={() => onSort(col)} style={{ background: "none", border: "none", color: "inherit", font: "inherit", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 4, textTransform: "inherit", letterSpacing: "inherit" }}>
        {label}<span style={{ opacity: active ? 1 : 0.25, fontSize: 10 }}>{active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

export default function ProductManager({ autoNew, onAutoNew }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [categoryId, setCategoryId] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);

  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);     // product | {} | null
  const [catalogueFor, setCatalogueFor] = useState(null); // product whose media/attributes/links are being edited
  const [showCategories, setShowCategories] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data, count: total } = await getProducts({
        search, status, categoryId: categoryId === "all" ? undefined : categoryId,
        page, pageSize: PAGE_SIZE, sortBy, sortDir,
      });
      setRows(data); setCount(total);
    } catch (e) {
      setError(e.message || "Could not load products.");
    } finally {
      setLoading(false);
    }
  }, [search, status, categoryId, sortBy, sortDir, page]);

  // Debounce so typing in search / changing filters doesn't spam the backend.
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const loadCategories = useCallback(async () => {
    try { const { data } = await getProductCategories(); setCategories(data); } catch { /* surfaced via products load */ }
  }, []);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  // Quick-action deep link ("+ New Product").
  useEffect(() => {
    if (autoNew) { setEditing({}); onAutoNew && onAutoNew(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoNew]);

  // Filter changes reset to page 1.
  const onSearch = (v) => { setSearch(v); setPage(1); };
  const onStatus = (v) => { setStatus(v); setPage(1); };
  const onCategory = (v) => { setCategoryId(v); setPage(1); };
  const onSort = (col) => { setPage(1); if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy(col); setSortDir("asc"); } };
  const clearFilters = () => { setSearch(""); setStatus("all"); setCategoryId("all"); setPage(1); };

  const save = async (payload) => {
    if (editing && editing.id) await updateProduct(editing.id, payload);
    else await createProduct(payload);
    setEditing(null);
    await load();
  };
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}" (${p.product_number})?`)) return;
    try { await deleteProduct(p.id); await load(); } catch (e) { setError(e.message); }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const from = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, count);
  const hasFilters = !!search || status !== "all" || categoryId !== "all";
  const selStyle = { ...S.input, width: "auto", minWidth: 140 };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input style={S.searchBar} placeholder="Search number, SKU, barcode, name…" value={search}
          onChange={(e) => onSearch(e.target.value)} aria-label="Search products" />
        <select style={selStyle} value={categoryId} onChange={(e) => onCategory(e.target.value)} aria-label="Filter by category">
          <option value="all">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select style={selStyle} value={status} onChange={(e) => onStatus(e.target.value)} aria-label="Filter by status">
          <option value="all">All statuses</option>
          {PRODUCT_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button style={S.btn("secondary")} onClick={() => setShowCategories(true)}>Manage Categories</button>
        <div style={{ flex: 1 }} />
        <span style={{ color: C.textMuted, fontSize: 12 }}>{count} product{count === 1 ? "" : "s"}</span>
        <button style={S.btn()} onClick={() => setEditing({})}>+ New Product</button>
      </div>

      {/* Error state */}
      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 12, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={load}>Retry</button>
        </div>
      )}

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <Th label="Number" col="number" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Name" col="name" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="SKU" col="sku" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th}>Category</th>
              <th style={S.th}>Price</th>
              <th style={S.th}>VAT</th>
              <Th label="Status" col="status" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th} aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={S.empty}>Loading…</td></tr>
            ) : count === 0 ? (
              <tr><td colSpan={8} style={S.empty}>
                {hasFilters ? (
                  <div>
                    No products match your filters.
                    <div style={{ marginTop: 10 }}><button style={S.btn("secondary")} onClick={clearFilters}>Clear filters</button></div>
                  </div>
                ) : (
                  <GetStartedCTA onLoaded={load} fallback={
                    <div>
                      <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>No products yet</div>
                      <div style={{ marginBottom: 12 }}>Add your first product to start building the catalogue.</div>
                      <button style={S.btn()} onClick={() => setEditing({})}>+ New Product</button>
                    </div>
                  } />
                )}
              </td></tr>
            ) : rows.map((p) => (
              <tr key={p.id} style={{ cursor: "pointer" }} onClick={() => setEditing(p)}>
                <td style={S.td}><span style={{ color: C.textMuted, fontFamily: "monospace", fontSize: 12 }}>{p.product_number}</span></td>
                <td style={S.td}>
                  <strong>{p.name}</strong>
                  {!p.in_stock && <span style={{ ...S.pill(C.textDim), marginLeft: 8, fontSize: 10 }}>out of stock</span>}
                  {p.description && <div style={{ color: C.textMuted, fontSize: 11, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.description}</div>}
                </td>
                <td style={S.td}>{p.sku || <span style={{ color: C.textDim }}>—</span>}</td>
                <td style={S.td}>{p.product_categories?.name || <span style={{ color: C.textDim }}>—</span>}</td>
                <td style={S.td}><strong>{money(p.wholesale_price)}</strong></td>
                <td style={S.td}>{p.vat_rate}%</td>
                <td style={S.td}><StatusBadge status={p.status} /></td>
                <td style={S.td} onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button style={S.btn("ghost")} title="Edit" aria-label={`Edit ${p.name}`} onClick={() => setEditing(p)}>✎</button>
                    <button style={S.btn("ghost")} title="Catalogue — media, attributes, related" aria-label={`Catalogue ${p.name}`} onClick={() => setCatalogueFor(p)}>🖼</button>
                    <button style={S.btn("ghost")} title="Delete" aria-label={`Delete ${p.name}`} onClick={() => del(p)}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {count > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 10 }}>
            <span style={{ color: C.textMuted, fontSize: 12 }}>Showing {from}–{to} of {count}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button style={{ ...S.btn("secondary"), opacity: page <= 1 ? 0.5 : 1 }} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page">← Prev</button>
              <span style={{ color: C.textMuted, fontSize: 12 }}>Page {page} of {totalPages}</span>
              <button style={{ ...S.btn("secondary"), opacity: page >= totalPages ? 0.5 : 1 }} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Next page">Next →</button>
            </div>
          </div>
        )}
      </div>

      {editing !== null && (
        <ProductForm initial={editing} categories={categories} onCancel={() => setEditing(null)} onSave={save} />
      )}
      {showCategories && (
        <CategoryManager onClose={() => setShowCategories(false)} onChanged={() => { loadCategories(); load(); }} />
      )}
      {catalogueFor && (
        <ProductCatalogueEditor product={catalogueFor} onClose={() => setCatalogueFor(null)} />
      )}
    </div>
  );
}
